import React, { useState, useCallback } from 'react';
import { useWhatsApp } from '../hooks/useWhatsApp';

const QRLandingPage = ({ 
  websiteUrl = "https://yusen-portfolio.preview.emergentagent.com",
  companyName = "Yusen Design & Contract Pte Ltd",
  tagline = "Interior Design & Renovation Specialist"
}) => {
  const [isTapped, setIsTapped] = useState(false);
  const [ripples, setRipples] = useState([]);
  const { openChat } = useWhatsApp();

  // Handle tap with ripple effect
  const handleTap = useCallback((e) => {
    // Create ripple
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const newRipple = { x, y, id: Date.now() };
    setRipples(prev => [...prev, newRipple]);

    // Visual feedback
    setIsTapped(true);
    
    // Navigate after brief delay for visual feedback
    setTimeout(() => {
      window.location.href = websiteUrl;
    }, 150);

    // Cleanup
    setTimeout(() => {
      setIsTapped(false);
      setRipples(prev => prev.filter(r => r.id !== newRipple.id));
    }, 600);
  }, [websiteUrl]);

  // Extract display URL
  const displayUrl = websiteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex flex-col items-center justify-center px-4 py-8 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      {/* Ambient Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/20 rounded-full blur-[120px] pointer-events-none" />

      {/* Content Container */}
      <div className="relative z-10 w-full max-w-md mx-auto text-center">
        
        {/* Company Logo/Name */}
        <div className="mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-4 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20">
            <span className="text-2xl font-bold text-white">Y</span>
          </div>
          <h2 className="text-white/80 text-sm font-medium tracking-widest uppercase">
            {companyName}
          </h2>
        </div>

        {/* Main Headline */}
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3 leading-tight">
          Tap to Visit Our Website
        </h1>
        
        {/* Subtext */}
        <p className="text-blue-200/80 text-base sm:text-lg mb-8">
          No scanning required — instant access
        </p>

        {/* QR-Style Tappable CTA */}
        <div className="relative inline-block mb-6">
          {/* Outer Glow Ring */}
          <div className={`absolute -inset-4 bg-blue-500/30 rounded-3xl blur-xl transition-all duration-300 ${isTapped ? 'opacity-100 scale-110' : 'opacity-50'}`} />
          
          {/* Pulsing Ring */}
          <div className="absolute -inset-2 border-2 border-blue-400/50 rounded-2xl animate-pulse" />
          
          {/* Main Button */}
          <button
            onClick={handleTap}
            onTouchStart={() => setIsTapped(true)}
            onTouchEnd={() => setTimeout(() => setIsTapped(false), 150)}
            className={`
              relative overflow-hidden
              w-64 h-64 sm:w-72 sm:h-72
              bg-white rounded-2xl
              shadow-2xl shadow-blue-500/25
              transform transition-all duration-200 ease-out
              ${isTapped ? 'scale-95' : 'scale-100 hover:scale-[1.02]'}
              cursor-pointer
              focus:outline-none focus:ring-4 focus:ring-blue-400/50
              active:scale-95
            `}
            aria-label={`Tap to visit ${companyName} website`}
            data-testid="qr-landing-cta"
          >
            {/* Ripple Effects */}
            {ripples.map(ripple => (
              <span
                key={ripple.id}
                className="absolute bg-blue-500/30 rounded-full animate-ripple pointer-events-none"
                style={{
                  left: ripple.x - 50,
                  top: ripple.y - 50,
                  width: 100,
                  height: 100,
                }}
              />
            ))}

            {/* QR Pattern Background */}
            <div className="absolute inset-0 p-4">
              <QRPattern />
            </div>

            {/* Center Logo Circle */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-white rounded-xl shadow-lg flex items-center justify-center border-4 border-[#1E3A5F]">
                <div className="text-center">
                  <span className="text-[#1E3A5F] text-2xl sm:text-3xl font-bold">YD</span>
                  <div className="text-[#1E3A5F]/60 text-[8px] sm:text-[10px] font-medium mt-0.5">DESIGN</div>
                </div>
              </div>
            </div>

            {/* Tap Indicator Overlay */}
            <div className={`
              absolute inset-0 flex items-center justify-center
              bg-[#1E3A5F]/95 rounded-2xl
              transition-opacity duration-200
              ${isTapped ? 'opacity-100' : 'opacity-0'}
            `}>
              <div className="text-white text-center">
                <svg className="w-12 h-12 mx-auto mb-2 animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                <span className="text-lg font-semibold">Opening...</span>
              </div>
            </div>
          </button>

          {/* Floating Tap Hint */}
          <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap">
            <div className="inline-flex items-center gap-2 bg-[#1E3A5F] text-white text-xs font-semibold px-4 py-2 rounded-full shadow-lg">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
              </span>
              TAP TO OPEN
            </div>
          </div>
        </div>

        {/* Trust Signal - Raw URL */}
        <div className="mt-10 mb-6">
          <p className="text-white/40 text-xs mb-2 uppercase tracking-wider">You'll be taken to</p>
          <a 
            href={websiteUrl}
            className="inline-flex items-center gap-2 text-blue-300 hover:text-blue-200 text-sm font-mono bg-white/5 px-4 py-2 rounded-lg border border-white/10 hover:border-white/20 transition-all"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            {displayUrl}
          </a>
        </div>

        {/* Tagline */}
        <p className="text-white/50 text-sm">
          {tagline}
        </p>

        {/* Secondary Actions */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href={`tel:+6567477740`}
            className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            Call Us
          </a>
          <span className="hidden sm:inline text-white/30">•</span>
          <button
            onClick={() => openChat({ page: 'qr-landing', context: 'footer' })}
            className="inline-flex items-center gap-2 text-white/70 hover:text-green-400 text-sm transition-colors"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            WhatsApp
          </button>
        </div>
      </div>

      {/* Bottom Brand */}
      <div className="absolute bottom-6 left-0 right-0 text-center">
        <p className="text-white/30 text-xs">
          © 2024 {companyName}
        </p>
      </div>

      {/* Custom Animations */}
      <style>{`
        @keyframes ripple {
          0% {
            transform: scale(0);
            opacity: 1;
          }
          100% {
            transform: scale(4);
            opacity: 0;
          }
        }
        .animate-ripple {
          animation: ripple 0.6s ease-out forwards;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

// QR-Style Pattern Component
const QRPattern = () => {
  // Generate deterministic QR-like pattern
  const pattern = [
    [1,1,1,1,1,1,1,0,1,0,1,0,0,1,1,1,1,1,1,1],
    [1,0,0,0,0,0,1,0,0,1,0,1,0,1,0,0,0,0,0,1],
    [1,0,1,1,1,0,1,0,1,0,0,0,1,1,0,1,1,1,0,1],
    [1,0,1,1,1,0,1,0,0,1,1,0,0,1,0,1,1,1,0,1],
    [1,0,1,1,1,0,1,0,1,0,1,1,0,1,0,1,1,1,0,1],
    [1,0,0,0,0,0,1,0,0,0,0,1,0,1,0,0,0,0,0,1],
    [1,1,1,1,1,1,1,0,1,0,1,0,1,1,1,1,1,1,1,1],
    [0,0,0,0,0,0,0,0,0,1,0,0,0,0,0,0,0,0,0,0],
    [1,0,1,1,0,1,1,0,1,0,1,1,0,0,1,0,1,0,1,0],
    [0,1,0,0,1,0,0,1,0,0,0,1,0,1,0,1,0,1,0,1],
    [1,0,1,0,1,0,1,0,1,0,0,0,0,0,1,0,1,0,1,0],
    [0,1,0,1,0,1,0,1,0,1,0,1,0,1,0,1,0,1,0,1],
    [0,0,0,0,0,0,0,0,1,0,1,0,1,0,0,1,0,1,1,0],
    [1,1,1,1,1,1,1,0,0,1,0,0,0,1,0,1,0,0,0,1],
    [1,0,0,0,0,0,1,0,1,0,1,1,0,0,1,0,1,1,0,0],
    [1,0,1,1,1,0,1,0,0,0,0,1,0,1,0,0,0,1,0,1],
    [1,0,1,1,1,0,1,0,1,1,0,0,1,0,1,1,0,0,1,0],
    [1,0,1,1,1,0,1,0,0,1,0,1,0,0,0,1,0,1,0,1],
    [1,0,0,0,0,0,1,0,1,0,1,0,1,1,0,0,1,0,1,0],
    [1,1,1,1,1,1,1,0,0,1,0,1,0,1,1,1,0,1,1,1],
  ];

  return (
    <div className="w-full h-full grid grid-cols-20 grid-rows-20 gap-[2px] p-2">
      {pattern.flat().map((cell, i) => (
        <div
          key={`qr-${i}`}
          className={`rounded-[2px] ${cell ? 'bg-[#1E3A5F]' : 'bg-transparent'}`}
        />
      ))}
    </div>
  );
};

export default QRLandingPage;
